import { message } from "antd";
import axiosInstance from "../../helpers/axios";
import { responseDataFormate } from "../../helpers/bycrypt";
import { API_URL } from "../../helpers/utils";
import {
  catchHandler,
  dispatchLoading,
  elseHandler,
} from "../../helpers/dispatch";

interface SageConnectParams {
  payload: {
    client_id: string;
    code: string;
    redirect_uri: string;
  };
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const sageConnectAPI =
  ({ payload, onSuccess, onError }: SageConnectParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(
        `${API_URL}/sage/connect`,
        payload,
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message || "Connected to Sage successfully");
        onSuccess?.(result.data);
      } else {
        message.error(result?.message || "Failed to connect to Sage");
        onError?.();
      }
    } catch (error: any) {
      console.error("Sage connection error:", error);
      onError?.();

      const errorMessage =
        error?.response?.data?.message || "Unable to connect to Sage";
      // message.error(errorMessage);
    }
  };

interface SageConnectionStatusParams {
  onSuccess: (isConnected: boolean) => void;
  onError: () => void;
}

// export const getSageConnectionStatusAPI =
//   ({ onSuccess, onError }: SageConnectionStatusParams) =>
//   async (dispatch: any) => {
//     try {
//       const response = await axiosInstance.get(`${API_URL}/sage/status`);
//       let result = responseDataFormate(response.data);

//       if (result?.success) {
//         dispatch({
//           type: "SAGE_CONNECTION_STATUS",
//           payload: result.data,
//         });
//         onSuccess?.(result.data.isConnected);
//       } else {
//         onError?.();
//       }
//     } catch (error: any) {
//       console.error("Sage status error:", error);
//       onError?.();
//     }
//   };

interface SageDisconnectParams {
  onSuccess: () => void;
  onError: () => void;
}

export const sageDisconnectAPI =
  ({ onSuccess, onError }: SageDisconnectParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(`${API_URL}/sage/disconnect`);
      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message || "Disconnected from Sage");
        onSuccess?.();
      } else {
        message.error(result?.message || "Failed to disconnect");
        onError?.();
      }
    } catch (error: any) {
      console.error("Sage disconnect error:", error);
      message.error("Unable to disconnect from Sage");
      onError?.();
    }
  };

export const getSageConnectionStatusAPI =
  () => async (dispatch: (action: any) => void) => {
    dispatchLoading(dispatch, "getSageStatus", true);
    axiosInstance
      .get(`/sage/status`)
      .then((response) => {
        let result = responseDataFormate(response.data);
        if (result?.success) {
          dispatch({
            type: "SAGE_CONNECTION_STATUS",
            payload: result.data,
          });
          dispatchLoading(dispatch, "getSageStatus", false);
        } else elseHandler(dispatch, "getSageStatus", result);
        dispatchLoading(dispatch, "getSageStatus", false);
      })
      .catch((err) => {
        catchHandler(dispatch, "getSageStatus")(err);
        // navigate("/auth/login");
      });
  };

interface SyncSageDataParams {
  onSuccess?: (data: any) => void;
  onError?: () => void;
  hideToast?: boolean;
}

export const syncSageDataAPI =
  ({ onSuccess, onError, hideToast = false }: SyncSageDataParams = {}) =>
  async (dispatch: any) => {
    dispatchLoading(dispatch, "syncSageData", true);
    try {
      const response = await axiosInstance.get(`${API_URL}/sage/sync`);
      let result = responseDataFormate(response.data);

      if (result?.success) {
        if (!hideToast) {
          message.success(result?.message || "Data synced successfully");
          if (result.data?.customer_count !== undefined) {
            message.info(`Synced ${result.data.customer_count} customers`);
          }
        }
        onSuccess?.(result.data);
      } else {
        if (!hideToast) {
          elseHandler(dispatch, "syncSageData", result);
        }
      }
      dispatchLoading(dispatch, "syncSageData", false);
    } catch (error: any) {
      if (!hideToast) {
        catchHandler(dispatch, "syncSageData")(error);
      }
      dispatchLoading(dispatch, "syncSageData", false);
      console.error("Sage sync error:", error);

      onError?.();
    } finally {
      dispatchLoading(dispatch, "syncSageData", false);
    }
  };

interface CreateSageClientParams {
  payload: any;
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const createSageClientAPI =
  ({ payload, onSuccess, onError }: CreateSageClientParams) =>
  async (dispatch: any) => {
    dispatchLoading(dispatch, "createSageClient", true);
    try {
      const response = await axiosInstance.post(`${API_URL}/sage/create-client`, payload);
      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message || "Client created successfully");
        onSuccess?.(result.data);
      } else {
        elseHandler(dispatch, "createSageClient", result);
        onError?.();
      }
      dispatchLoading(dispatch, "createSageClient", false);
    } catch (error: any) {
      catchHandler(dispatch, "createSageClient")(error);
      dispatchLoading(dispatch, "createSageClient", false);
      console.error("Create Sage client error:", error);
      onError?.();
    } finally {
      dispatchLoading(dispatch, "createSageClient", false);
    }
  };

interface UpdateSageClientParams {
  payload: any;
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const updateSageClientAPI =
  ({ payload, onSuccess, onError }: UpdateSageClientParams) =>
  async (dispatch: any) => {
    dispatchLoading(dispatch, "updateSageClient", true);
    try {
      const response = await axiosInstance.put(`${API_URL}/sage/update-client`, payload);
      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message || "Client updated successfully");
        onSuccess?.(result.data);
      } else {
        elseHandler(dispatch, "updateSageClient", result);
        onError?.();
      }
      dispatchLoading(dispatch, "updateSageClient", false);
    } catch (error: any) {
      catchHandler(dispatch, "updateSageClient")(error);
      dispatchLoading(dispatch, "updateSageClient", false);
      console.error("Update Sage client error:", error);
      onError?.();
    } finally {
      dispatchLoading(dispatch, "updateSageClient", false);
    }
  };

interface SyncSageTaxesParams {
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const syncSageTaxesAPI =
  ({ onSuccess, onError }: SyncSageTaxesParams = {}) =>
  async (dispatch: any) => {
    dispatchLoading(dispatch, "syncSageTaxes", true);
    try {
      const response = await axiosInstance.post(`${API_URL}/sage-taxes/sync`);
      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message || "Taxes synced successfully");
        dispatch(getSageConnectionStatusAPI());
        onSuccess?.(result.data);
      } else {
        elseHandler(dispatch, "syncSageTaxes", result);
        onError?.();
      }
      dispatchLoading(dispatch, "syncSageTaxes", false);
    } catch (error: any) {
      catchHandler(dispatch, "syncSageTaxes")(error);
      dispatchLoading(dispatch, "syncSageTaxes", false);
      console.error("Sage taxes sync error:", error);
      onError?.();
    } finally {
      dispatchLoading(dispatch, "syncSageTaxes", false);
    }
  };

interface SyncSageCountriesParams {
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const syncSageCountriesAPI =
  ({ onSuccess, onError }: SyncSageCountriesParams = {}) =>
  async (dispatch: any) => {
    dispatchLoading(dispatch, "syncSageCountries", true);
    try {
      const response = await axiosInstance.post(`${API_URL}/sage-country/sync`);
      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message || "Countries synced successfully");
        dispatch(getSageConnectionStatusAPI());
        onSuccess?.(result.data);
      } else {
        elseHandler(dispatch, "syncSageCountries", result);
        onError?.();
      }
      dispatchLoading(dispatch, "syncSageCountries", false);
    } catch (error: any) {
      catchHandler(dispatch, "syncSageCountries")(error);
      dispatchLoading(dispatch, "syncSageCountries", false);
      console.error("Sage countries sync error:", error);
      onError?.();
    } finally {
      dispatchLoading(dispatch, "syncSageCountries", false);
    }
  };

